<!-- DateInput.vue -->
<template>
  <div class="relative z-0 mb-2 w-full group" :class="statusClass" :required="required">
    <input type="date" :name="name" :id="name"
      class="w-full p-3 border rounded-md mb-8 invalid:border-red-500 invalid:!bg-red-50 focus:border-tertiary/50 focus:outline-0 peer transition-all group-[.invalid]:bg-red-50 group-[.invalid]:text-red-500 group-[.invalid]:border-red-500"
      :class="isEmpty && required
          ? '!border-red-500 bg-red-50 text-red-500'
          : 'border-secondary/50 text-tertiary'
        " :placeholder="placeholder" :disabled="disabled" :min="dmin" :max="dmax" v-model="date"
      @change="validateDate" />
    <div class="absolute bottom-1 text-primary text-sm">{{ message }}</div>
  </div>
</template>

<script>
/** Komponente zur Darstellung eines Datumsfeldes mit Eingabemöglichkeit, wobei Datumsgrenzen vorgegeben werden können
 * @displayName Date Input
 */
export default {
  name: "DateInput",

  props: {
    /**
     * Allgemeine Props
     */
    /** @type {String} Name des Input-Feldes */
    name: { type: String, default: "dateInput" },
    /** @type {String} Zusätzliche CSS-Klassen für Statusstyling */
    statusClass: { type: String, default: "" },
    /** @type {Boolean} Deaktiviert das Input-Feld */
    disabled: { type: Boolean, default: false },
    /** @type {Boolean} Markiert das Feld als erforderlich */
    required: { type: Boolean, default: false },
    /** @type {String} v-model Binding für das ausgewählte Datum im Format YYYY-MM-DD */
    modelValue: { type: String, default: "" },
    /** @type {String} Placeholder-Text für das Eingabefeld */
    placeholder: { type: String, default: "Datum" },

    /**
     * Datumsgrenzen
     */
    /**
     * @type {String}
     * Minimales Datum (ISO-Format) oder relative Angabe als Zahl
     * (z.B. "+5" für 5 Tage in der Zukunft, "-7" für 7 Tage in der Vergangenheit)
     * Default: 7 Tage vor heute
     */
    dmin: {
      type: String,
      default: () => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().slice(0, 10);
      },
    },
    /**
     * @type {String}
     * Maximales Datum (ISO-Format) oder relative Angabe als Zahl
     * Default: 7 Tage nach heute
     */
    dmax: {
      type: String,
      default: () => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        return d.toISOString().slice(0, 10);
      },
    },

    /**
     * @type {Boolean}
     * Wenn true: Erzwingt automatisch den 1. des Folgemonats,
     * wenn ein anderes Datum ausgewählt wird
     */
    forceFirstOfNextMonth: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @event update:modelValue - Emittiert das neue Datum, wenn es valid ist
   * @event dateChecked - Emittiert Validierungsstatus ("false" für valid, "true" für invalid)
   */
  emits: ["update:modelValue", "dateChecked"],

  data() {
    return {
      date: this.modelValue,
      message: "",
    };
  },

  computed: {
    isEmpty() {
      return this.date === "";
    },
  },

  watch: {
    modelValue(val) {
      if (val !== this.date) this.date = val;
    },
  },

  mounted() {
    /* leeres Feld sofort vorbelegen */
    if (this.forceFirstOfNextMonth && !this.date) this.setFirstNextMonth();
    this.validateDate();
  },

  methods: {
    /************************ Hilfen ************************************/
    stringToDate(s) {
      if (!s) return new Date();
      if (/^[+-]\d+$/.test(s)) {
        const d = new Date();
        d.setDate(d.getDate() + parseInt(s, 10));
        return d;
      }
      return new Date(s);
    },
    setFirstNextMonth() {
      const today = new Date();
      /* UTC erzeugen, um TZ-Shift zu vermeiden */
      const first = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1)
      );
      this.date = first.toISOString().substring(0, 10);
      this.$emit("update:modelValue", this.date);
    },

    /*********************** Haupt-Validierung **************************/
    validateDate() {
      /* -------- 1. Eingabe einlesen / Format prüfen ---------------- */
      const isoRE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
      const fmtOK = isoRE.test(this.date);
      let d = fmtOK ? new Date(this.date) : new Date(); // Fallback

      /* -------- 2. 1.-Korrektur erzwingen -------------------------- */
      if (this.forceFirstOfNextMonth) {
        if (d.getUTCDate() !== 1) {
          /* ausgewählter Tag ≠ 1  ⇒  auf 1. des Folgemonats springen */
          d.setUTCMonth(d.getUTCMonth() + 1, 1);
        }
        /* ISO-String yyyy-MM-dd ohne TZ-Shift */
        this.date = d.toISOString().substring(0, 10);
      }

      /* -------- 3. Bereichs- und 1.-Tag-Prüfung --------------------- */
      const min = this.stringToDate(this.dmin);
      const max = this.stringToDate(this.dmax);
      const ok = d >= min && d <= max;
      this.message = ok ? "" : "Das Datum liegt nicht im zulässigen Bereich.";
      /* nur 1. eines Monats ist gültig */
      if (ok && this.forceFirstOfNextMonth && d.getUTCDate() !== 1) {
        this.message = "Bitte einen 1. eines zulässigen Monats wählen.";
      }
      this.$emit("dateChecked", ok ? "false" : "true");
      if (ok) this.$emit("update:modelValue", this.date);
    },
  },
};
</script>
